import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { ensureThemeJsonArray, getDvSourceImageUrl } from "../../functions/themeAssets";
import { normalizeCategoryName } from "./dragDropUtils";
import { getQuizQuestions } from "./stageContent";
import {
  DEMO_PLANS_URL,
  StageLoader,
  StageShell,
  useStageRun,
} from "./stageShared";
import { nyAlert, showAnswerFeedback } from "../nyPopups";

/** Stage 1 — image quiz (Admin → Game Content → Quiz). */
const STAGE_NUM = 1;
const OPTION_LETTERS = ["A", "B", "C", "D", "E", "F"];
const DEFAULT_QUIZ_HEADING = "Quiz";

export default function Game() {
  const run = useStageRun(STAGE_NUM);
  const { themeData, isLoading, isDemoBypass, pointsPerCorrect, addPoints, finishStage, startTimer } =
    run;

  // Quiz content from the theme (Admin → Game Content → Quiz).
  const questions = useMemo(() => getQuizQuestions(themeData), [themeData]);
  const categoryNames = useMemo(
    () =>
      ensureThemeJsonArray(themeData?.categories)
        .map((c) => String(c?.name ?? "").trim())
        .filter(Boolean),
    [themeData]
  );
  const heading = String(themeData?.quiz_title || "").trim() || DEFAULT_QUIZ_HEADING;
  const subheading = String(themeData?.quiz_subtitle || "").trim();

  const [qIndex, setQIndex] = useState(0);
  const [selected, setSelected] = useState(null);
  const [revealed, setRevealed] = useState(false);
  const [busy, setBusy] = useState(false);

  const qIndexRef = useRef(0);
  const answersRef = useRef([]);
  const finished = useRef(false);

  // Nothing configured for this stage — skip straight to the next one.
  useEffect(() => {
    if (isLoading || questions.length > 0 || finished.current) return;
    finished.current = true;
    finishStage();
  }, [isLoading, questions.length, finishStage]);

  useEffect(() => {
    if (!isLoading && questions.length) startTimer();
  }, [isLoading, questions.length, startTimer]);

  const currentQuestion = questions[qIndex] || null;

  // Each question carries its own 2–4 options; legacy rows without any fall back
  // to the theme's configured categories.
  const options = useMemo(() => {
    if (currentQuestion?.options?.length) return currentQuestion.options;
    return categoryNames;
  }, [categoryNames, currentQuestion]);

  const advance = useCallback(() => {
    const next = qIndexRef.current + 1;
    if (next >= questions.length) {
      finished.current = true;
      finishStage({ answers: answersRef.current });
      return;
    }
    qIndexRef.current = next;
    setQIndex(next);
    setSelected(null);
    setRevealed(false);
    setBusy(false);
  }, [questions.length, finishStage]);

  // Tapping an option answers the question (as in the reference design — no Next button).
  const handleAnswer = useCallback(
    async (optionIndex) => {
      if (!currentQuestion || busy || finished.current) return;
      setBusy(true);
      setSelected(optionIndex);

      const chosenLabel = options[optionIndex];
      const correct =
        normalizeCategoryName(chosenLabel) === normalizeCategoryName(currentQuestion.correctAnswer);
      const isFirstQuestion = answersRef.current.length === 0;

      answersRef.current.push({
        questionId: currentQuestion.id,
        questionText: currentQuestion.question_name || currentQuestion.question_image,
        userAnswer: chosenLabel,
        correctAnswer: currentQuestion.correctAnswer,
        isCorrect: correct,
        questionNumber: answersRef.current.length + 1,
        answeredAt: new Date().toISOString(),
      });

      if (correct) addPoints(pointsPerCorrect);

      // Show right/wrong on the option pills behind the popup.
      setRevealed(true);

      await showAnswerFeedback({
        correct,
        points: pointsPerCorrect,
        answer: currentQuestion.correctAnswer,
      });

      // Demo sessions only get a taste of the first question, then a CTA to subscribe.
      if (isDemoBypass && isFirstQuestion) {
        finished.current = true;
        await nyAlert({
          kind: "star",
          title: "Thanks for playing!",
          text: "Subscribe to any PLAN to play with your peers.",
          allowOutsideClick: false,
          allowEscapeKey: false,
        });
        window.location.assign(DEMO_PLANS_URL);
        return;
      }

      advance();
    },
    [currentQuestion, busy, options, isDemoBypass, pointsPerCorrect, addPoints, advance]
  );

  if (isLoading || !currentQuestion) return <StageLoader />;

  const correctKey = normalizeCategoryName(currentQuestion.correctAnswer);

  return (
    <StageShell run={run} heading={heading} subheading={subheading} step={qIndex}>
      <main key={currentQuestion.id} className="ny-content q1-stage">
        <figure className="q1-media">
          {currentQuestion.question_image ? (
            <img
              src={getDvSourceImageUrl(currentQuestion.question_image)}
              alt={`Question ${qIndex + 1}`}
              className="q1-media-image"
              draggable={false}
            />
          ) : (
            <figcaption className="q1-media-text">{currentQuestion.question_name}</figcaption>
          )}
        </figure>

        <div
          className={`ny-options ny-options--${Math.min(options.length, 4)}`}
          role="group"
          aria-label="Answer options"
        >
          {options.map((label, i) => {
            const isCorrect = normalizeCategoryName(label) === correctKey;
            let state = "";
            if (revealed && isCorrect) state = " is-correct";
            else if (revealed && selected === i) state = " is-wrong";
            return (
              <button
                key={`${label}-${i}`}
                type="button"
                className={`ny-option${state}`}
                onClick={() => handleAnswer(i)}
                disabled={busy}
              >
                <span className="ny-option-letter">{OPTION_LETTERS[i]}</span>
                <span className="ny-option-label">{label}</span>
              </button>
            );
          })}
        </div>
      </main>
    </StageShell>
  );
}
